<template>
  <div>
    <div>
      <b-row>
        <b-col
          cols="12"
          md="6"
          lg="4"
          class="mb-1 mb-md-0"
        >
          <StatisticCardHorizontal
            :data="totalCommission"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          lg="4"
          class="mb-1 mb-md-0"
        >
          <StatisticCardHorizontal
            :data="deliveryIncome"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          lg="4"
          class="mb-1 mb-md-0"
        >
          <StatisticCardHorizontal
            :data="returnIncome"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          lg="4"
          class="mb-1 mb-md-0"
        >
          <StatisticCardHorizontal
            :data="deliveryCharge"
          />
        </b-col>
        <b-col
          cols="12"
          md="6"
          lg="4"
          class="mb-1 mb-md-0"
        >
          <StatisticCardHorizontal
            :data="total_amount"
          />
        </b-col>
      </b-row>
    </div>
    <div>
      <b-card
        no-body
      >
        <div
          class="m-2"
        >
          <b-row>
            <b-col
              class="d-flex align-items-center justify-content-end mb-1 mb-md-0"
            >
              <b-button
                id="generate-current-month-sales-btn"
                variant="primary"
                class="mr-1"
                :disabled="isGenerateButtonBlocked || generatingCurrentMonthSales"
                @click="onGenerateCurrentMonthSales"
              >
                {{ generatingCurrentMonthSales ? 'Generating...' : generateButtonLabel }}
              </b-button>
              <b-popover
                target="generate-current-month-sales-btn"
                triggers="hover focus"
                placement="bottom"
                :show.sync="showGeneratedSalesPopover"
              >
                <template #title>
                  Show Current Month Sales
                </template>
                <div
                  v-if="generatedSalesTooltipRows.length"
                  class="generated-sales-tooltip"
                >
                  <div
                    v-for="(row, index) in generatedSalesTooltipRows"
                    :key="`generated-sales-row-${index}`"
                    class="generated-sales-tooltip__row"
                  >
                    {{ row }}
                  </div>
                </div>
              </b-popover>
              <ReportTableButtons
                :items="items"
                :json_fieldz="json_fields"
                :name="`MonthlySales - Page ${page}.xls`"
                :bulkname="`MonthlySales.xls`"
                :fetch="fetchDataForMonthlySalesNoPagination"
                @refresh="onClickRefresh"
              />
            </b-col>
          </b-row>
        </div>
        <b-overlay
          id="overlay-background"
          blur="10px"
          opacity="0.30"
          rounded="sm"
          variant="light"
        >
          <b-table
            striped
            hover
            responsive
            :per-page="perPage"
            :current-page="currentPage"
            :items="items"
            :fields="fields"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            show-empty
            sticky-header="100vh"
            @sort-changed="sortChanged"
          >
            <template #empty>
              <TableDataFetching
                :rows="items"
                :data-loading="dataLoading"
              />
            </template>
            <template #head()="{ field }">
              <div
                class="sortable-header"
              >
                <span class="header-text">{{ field.label }}</span>
                <feather-icon
                  v-if="field.sortable"
                  :icon="sort === field.key
                    ? 'ArrowUpIcon'
                    : 'ArrowDownIcon'"
                  size="14"
                />
              </div>
            </template>
            <template #cell(total_commission)="data">
              {{ __numberWithCommas(data.value, true) }}
            </template>
            <template #cell(delivery_income)="data">
              {{ __numberWithCommas(data.value, true) }}
            </template>
            <template #cell(return_income)="data">
              {{ __numberWithCommas(data.value, true) }}
            </template>
            <template #cell(total_amount)="data">
              {{ __numberWithCommas(data.value, true) }}
            </template>
          </b-table>
        </b-overlay>
        <div class="mx-2 mb-2">
          <b-row>

            <b-col
              cols="12"
              md="3"
              class="d-flex align-items-center justify-content-sm-start"
            >
              <label>Per page</label>
              <v-select
                v-model="perPage"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="pageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block ml-1 width-100 mr-1"
              />
            </b-col>
            <!-- Pagination -->
            <b-col
              cols="12"
              md="9"
              class="d-flex align-items-center justify-content-sm-end"
            >
              <span class="text-muted pagination-text  mt-1 mt-md-0 mr-1">Showing {{ meta.from }} to {{ meta.to }} of {{ meta.total }} entries</span>
              <b-pagination
                v-model="page"
                :total-rows="(meta.total)? meta.total : 0"
                :per-page="perPage"
                first-number
                last-number
                class="mb-0 mt-1 mt-sm-0"
                prev-class="prev-item"
                next-class="next-item"
              >
                <template #prev-text>
                  <feather-icon
                    icon="ChevronLeftIcon"
                    size="18"
                  />
                </template>
                <template #next-text>
                  <feather-icon
                    icon="ChevronRightIcon"
                    size="18"
                  />
                </template>
              </b-pagination>

            </b-col>

          </b-row>
        </div>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCol,
  BPagination,
  BRow,
  BTable,
  BButton,
  BPopover,
  VBModal,
  BOverlay,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import Filter from '@core/mixins/filter'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import StatisticCardHorizontal from '@/components/report/StatisticCardHorizontal.vue'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import tableMixin from '@core/mixins/tableMixin'

const ReportRepository = RepositoryFactory.get('report')

export default {
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  components: {
    'v-select': vSelect,
    BCard,
    BTable,
    BRow,
    BCol,
    BPagination,
    BOverlay,
    BButton,
    BPopover,
    StatisticCardHorizontal,
  },
  mixins: [Filter, SuccessMessage, ErrorMessage, tableMixin],
  data() {
    return {
      filters: {
        'orders.created_at': {
          startDate: null,
          endDate: null,
        },
      },
      sort: '',
      perPage: 20,
      pageOptions: [10, 20, 50],
      totalRows: 1,
      page: 1,
      meta: {},
      sortBy: '',
      sortDesc: false,
      dataLoading: false,
      sortDirection: 'asc',
      filter: null,
      filterOn: [],
      infoModal: {
        id: 'info-modal',
        title: '',
        content: '',
      },
      fields: [
        {
          key: 'year', label: 'Year', thClass: 'text-center', tdClass: 'text-center',
        },
        {
          key: 'month', label: 'Month', thClass: 'text-left', tdClass: 'text-left',
        },
        {
          key: 'total_commission', label: 'Total Commission', sortable: true, thClass: 'text-right', tdClass: 'text-right',
        },
        {
          key: 'delivery_income', label: 'Delivery Income', sortable: true, thClass: 'text-right', tdClass: 'text-right',
        },
        {
          key: 'return_income', label: 'Return Income', sortable: true, thClass: 'text-right', tdClass: 'text-right',
        },
        {
          key: 'total_amount', label: 'Total Amount', sortable: false, thClass: 'text-right', tdClass: 'text-right',
        },
      ],
      json_fields: {
        Year: 'year',
        Month: 'month',
        'Total Commission': 'total_commission',
        'Delivery Income': 'delivery_income',
        'Return Income': 'return_income',
        'Total Amount': 'total_amount',
      },
      items: [],
      // Card Data
      deliveryCharge: [
        {
          icon: 'TruckIcon',
          statistic: 0,
          title: 'Total Delivery Charge',
          color: 'primary',
        },
      ],
      totalCommission: [
        {
          icon: 'ActivityIcon',
          statistic: 0,
          title: 'Total Commission',
          color: 'warning',
        },
      ],
      deliveryIncome: [
        {
          icon: 'DollarSignIcon',
          statistic: 0,
          title: 'Delivery Income',
          color: 'success',
        },
      ],
      returnIncome: [
        {
          icon: 'RefreshCwIcon',
          statistic: 0,
          title: 'Return Income',
          color: 'secondary',
        },
      ],
      total_amount: [
        {
          icon: 'EditIcon',
          statistic: 0,
          title: 'Total Amount',
          color: 'info',
        },
      ],
      currentMonthSalesData: null,
      generatingCurrentMonthSales: false,
      showGeneratedSalesPopover: false,
      generateButtonCooldownUntil: null,
      generateButtonCooldownRemaining: 0,
      generateButtonCooldownInterval: null,
    }
  },
  computed: {
    sortOptions() {
      // Create an options list from our fields
      return this.fields
        .filter(f => f.sortable)
        .map(f => ({ text: f.label, value: f.key }))
    },
    isGenerateButtonBlocked() {
      return this.generateButtonCooldownRemaining > 0
    },
    generateButtonLabel() {
      if (!this.isGenerateButtonBlocked) return 'Generate'
      return `Generate (${this.formatCooldownTime(this.generateButtonCooldownRemaining)})`
    },
    generatedSalesTooltipRows() {
      if (!this.currentMonthSalesData) return []
      const sales = this.currentMonthSalesData
      return [
        `Year: ${sales.year ?? '-'}`,
        `Month: ${sales.month ?? '-'}`,
        `Total Delivery Charge: ${this.formatAmount(sales.total_delivery_charge ?? 0)}`,
        `Total Commission: ${this.formatAmount(sales.total_commission ?? 0)}`,
        `Delivery Income: ${this.formatAmount(sales.delivery_income ?? 0)}`,
        `Return Income: ${this.formatAmount(sales.return_income ?? 0)}`,
        `Total Amount: ${this.formatAmount(sales.total_amount ?? 0)}`,
      ]
    },
  },
  watch: {
    options: {
      handler() {
        this.fetchDataForMonthlySales()
      },
    },
    deep: true,
    page() {
      this.fetchDataForMonthlySales()
    },
    perPage() {
      this.fetchDataForMonthlySales()
    },
  },
  mounted() {
    // Set the initial number of items
    this.totalRows = this.items.length
    this.fetchDataForMonthlySales()
    this.fetchDataForMonthlySalesReportOverview()
    this.updateFilterCount()
    this.initGenerateButtonCooldown()
  },
  beforeDestroy() {
    this.clearGenerateButtonCooldownInterval()
  },
  methods: {
    async fetchDataForMonthlySales() {
      this.dataLoading = true
      this.items = []

      // Build the filter query string
      const filterQuery = this.buildFilterQuery()

      try {
        // Include the filterQuery in the API call
        const { data } = await ReportRepository.getmonthlySalesReport(this.page, filterQuery, this.perPage)
        this.items = data.data
        this.meta = data.meta
      } catch (e) {
        this.convertAndNotifyError(e)
      }

      this.dataLoading = false
    },
    async fetchDataForMonthlySalesNoPagination() {
      try {
        const filterQuery = this.buildFilterQuery()
        const { data } = (await ReportRepository.getmonthlySalesReportNoPagination(filterQuery))
        return data.data
      } catch (e) {
        this.convertAndNotifyError(e)
        return []
      }
    },
    async fetchDataForMonthlySalesReportOverview() {
      try {
        const { data } = await ReportRepository.getmonthlySalesReportOverview()
        const totalAmounts = data?.total_amounts ?? data?.data?.total_amounts ?? {}
        this.updateStatistics(totalAmounts)
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },
    updateStatistics(totalAmounts) {
      if (!totalAmounts) return
      this.deliveryCharge[0].statistic = totalAmounts.total_delivery_charge ?? 0
      this.totalCommission[0].statistic = totalAmounts.total_commission ?? 0
      this.$set(this.deliveryIncome[0], 'statistic', totalAmounts.delivery_income ?? 0)
      this.$set(this.returnIncome[0], 'statistic', totalAmounts.return_income ?? 0)
      this.total_amount[0].statistic = totalAmounts.total_amount ?? 0
    },
    resetModal() {
      this.clearOnClick()
    },
    info(item, index, button) {
      this.infoModal.title = `Row index: ${index}`
      this.infoModal.content = JSON.stringify(item, null, 2)
      this.$root.$emit('bv::show::modal', this.infoModal.id, button)
    },
    resetInfoModal() {
      this.infoModal.title = ''
      this.infoModal.content = ''
    },
    handleChangePage(page) {
      this.page = page
      this.fetchDataForMonthlySales()
    },
    applyFilters() {
      this.fetchDataForMonthlySales()
      this.updateFilterCount()
    },
    clearFilters() {
      this.filters = {
        'orders.created_at': {
          startDate: null,
          endDate: null,
        },
      }
      this.$nextTick(() => {
        this.fetchDataForMonthlySales()
      })
      this.updateFilterCount()
    },
    onClickRefresh() {
      this.fetchDataForMonthlySales()
    },
    async onGenerateCurrentMonthSales() {
      if (this.isGenerateButtonBlocked || this.generatingCurrentMonthSales) return

      this.generatingCurrentMonthSales = true
      this.showGeneratedSalesPopover = false

      try {
        const { data } = await ReportRepository.getCurrentMonthlySalesReport()
        this.currentMonthSalesData = data?.data ?? data ?? null
        this.showGeneratedSalesPopover = !!this.currentMonthSalesData
        this.startGenerateButtonCooldown()
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.generatingCurrentMonthSales = false
      }
    },
    initGenerateButtonCooldown() {
      const savedCooldown = localStorage.getItem('monthlySalesGenerateCooldownUntil')
      if (savedCooldown) {
        const parsed = Number(savedCooldown)
        if (!Number.isNaN(parsed) && parsed > Date.now()) {
          this.generateButtonCooldownUntil = parsed
          this.startGenerateButtonCooldownInterval()
        } else {
          localStorage.removeItem('monthlySalesGenerateCooldownUntil')
        }
      }
      this.updateGenerateButtonCooldownRemaining()
    },
    startGenerateButtonCooldown() {
      this.generateButtonCooldownUntil = Date.now() + (5 * 60 * 1000)
      localStorage.setItem('monthlySalesGenerateCooldownUntil', String(this.generateButtonCooldownUntil))
      this.updateGenerateButtonCooldownRemaining()
      this.startGenerateButtonCooldownInterval()
    },
    startGenerateButtonCooldownInterval() {
      this.clearGenerateButtonCooldownInterval()
      this.generateButtonCooldownInterval = setInterval(() => {
        this.updateGenerateButtonCooldownRemaining()
      }, 1000)
    },
    clearGenerateButtonCooldownInterval() {
      if (!this.generateButtonCooldownInterval) return
      clearInterval(this.generateButtonCooldownInterval)
      this.generateButtonCooldownInterval = null
    },
    updateGenerateButtonCooldownRemaining() {
      if (!this.generateButtonCooldownUntil) {
        this.generateButtonCooldownRemaining = 0
        return
      }
      const remainingSeconds = Math.ceil((this.generateButtonCooldownUntil - Date.now()) / 1000)
      this.generateButtonCooldownRemaining = Math.max(remainingSeconds, 0)

      if (this.generateButtonCooldownRemaining <= 0) {
        this.generateButtonCooldownUntil = null
        localStorage.removeItem('monthlySalesGenerateCooldownUntil')
        this.clearGenerateButtonCooldownInterval()
      }
    },
    formatCooldownTime(seconds) {
      const minutes = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
    },
    formatAmount(value) {
      // eslint-disable-next-line no-underscore-dangle
      const numberWithCommas = this.__numberWithCommas
      if (typeof numberWithCommas === 'function') {
        return numberWithCommas.call(this, value, true)
      }
      return value
    },
  },
}
</script>
<style lang="scss">
@import '../../@core/scss/vue/libs/vue-select.scss';

.generated-sales-tooltip__row {
  line-height: 1.3;
}

.generated-sales-tooltip__row + .generated-sales-tooltip__row {
  margin-top: 0.2rem;
}
</style>
